import * as THREE from 'three';
import { CONFIG } from '../app/config.js';

export class PlayerCharacter {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'PlayerCharacter';

    this.currentStyle = null;

    // Sub-groups for modular assembly
    this.bodyGroup = new THREE.Group();
    this.headGroup = new THREE.Group();
    this.hairGroup = new THREE.Group();
    this.hatGroup = new THREE.Group();
    this.accessoryGroup = new THREE.Group();
    this.leftArmGroup = new THREE.Group();
    this.rightArmGroup = new THREE.Group();
    this.leftLegGroup = new THREE.Group();
    this.rightLegGroup = new THREE.Group();

    // Reusable Materials (Colors updated in place)
    this.skinMaterial = new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0.1 });
    this.upperClothMaterial = new THREE.MeshStandardMaterial({ roughness: 0.6, metalness: 0.1 });
    this.lowerClothMaterial = new THREE.MeshStandardMaterial({ roughness: 0.7, metalness: 0.1 });
    this.hairMaterial = new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0.05 });
    this.shoeMaterial = new THREE.MeshStandardMaterial({ color: 0x111625, roughness: 0.5 });
    this.hatMaterial = new THREE.MeshStandardMaterial({ roughness: 0.6, metalness: 0.1 });
    this.goldMaterial = new THREE.MeshStandardMaterial({ color: 0xffd700, roughness: 0.3, metalness: 0.8 });
    this.darkAccentMaterial = new THREE.MeshBasicMaterial({ color: 0x111111 });

    // Animation state variables
    this.animTime = 0;
    this.isRunning = false;
    this.isJumping = false;
    this.targetLaneX = 0;
    this.absorbPulseTime = 0;

    // Track active meshes for disposal
    this.dynamicMeshes = [];
  }

  create(normalizedStyle) {
    this.scene.add(this.group);
    this.group.position.set(0, 0, CONFIG.RUNNER.FIXED_Z);

    // Build hierarchy
    this.group.add(this.bodyGroup);
    this.group.add(this.headGroup);
    this.headGroup.add(this.hairGroup);
    this.headGroup.add(this.hatGroup);
    this.headGroup.add(this.accessoryGroup);
    this.group.add(this.leftArmGroup);
    this.group.add(this.rightArmGroup);
    this.group.add(this.leftLegGroup);
    this.group.add(this.rightLegGroup);

    this.applyStyle(normalizedStyle);
  }

  applyStyle(normalizedStyle) {
    this.currentStyle = normalizedStyle;

    // Update material colors in-place (no re-allocations)
    this.skinMaterial.color.set(normalizedStyle.skinColor);
    this.upperClothMaterial.color.set(normalizedStyle.primaryColor);
    this.lowerClothMaterial.color.set(normalizedStyle.secondaryColor);
    this.hairMaterial.color.set(normalizedStyle.hairColor);

    // Clear dynamic sub-groups and dispose old geometry
    this.clearSubGroup(this.bodyGroup);
    this.clearSubGroup(this.headGroup, [this.hairGroup, this.hatGroup, this.accessoryGroup]);
    this.clearSubGroup(this.hairGroup);
    this.clearSubGroup(this.hatGroup);
    this.clearSubGroup(this.accessoryGroup);
    this.clearSubGroup(this.leftArmGroup);
    this.clearSubGroup(this.rightArmGroup);
    this.clearSubGroup(this.leftLegGroup);
    this.clearSubGroup(this.rightLegGroup);

    // 1. Build Body & Torso according to silhouette ('female' | 'male')
    this.buildBody(normalizedStyle.body);

    // 2. Build Head & Face
    this.buildHead();

    // 3. Build Limbs
    this.buildLimbs(normalizedStyle.body);

    // 4. Build Hair Style
    this.buildHair(normalizedStyle.hair);

    // 5. Build Hat
    this.buildHat(normalizedStyle.hat);

    // 6. Build Accessory
    this.buildAccessory(normalizedStyle.accessory);
  }

  clearSubGroup(group, preserveChildren = []) {
    const toRemove = [];
    for (let i = 0; i < group.children.length; i++) {
      const child = group.children[i];
      if (!preserveChildren.includes(child)) {
        toRemove.push(child);
      }
    }
    for (const child of toRemove) {
      group.remove(child);
      if (child.geometry) {
        child.geometry.dispose();
      }
    }
  }

  buildBody(bodyType) {
    const isFemale = bodyType === 'female';
    const torsoWidth = isFemale ? 0.52 : 0.64;
    const torsoHeight = 0.62;
    const torsoDepth = isFemale ? 0.32 : 0.38;

    // Upper Torso (Primary clothing color)
    const torsoGeo = new THREE.BoxGeometry(torsoWidth, torsoHeight, torsoDepth);
    const torsoMesh = new THREE.Mesh(torsoGeo, this.upperClothMaterial);
    torsoMesh.position.y = 1.05;
    this.bodyGroup.add(torsoMesh);

    // Hips/Pelvis (Secondary clothing color)
    const hipsWidth = isFemale ? 0.54 : 0.6;
    const hipsGeo = new THREE.BoxGeometry(hipsWidth, 0.22, torsoDepth * 0.95);
    const hipsMesh = new THREE.Mesh(hipsGeo, this.lowerClothMaterial);
    hipsMesh.position.y = 0.7;
    this.bodyGroup.add(hipsMesh);
  }

  buildHead() {
    this.headGroup.position.set(0, 1.55, 0);

    // Neck
    const neckGeo = new THREE.CylinderGeometry(0.12, 0.14, 0.18, 6);
    const neckMesh = new THREE.Mesh(neckGeo, this.skinMaterial);
    neckMesh.position.y = -0.12;
    this.headGroup.add(neckMesh);

    // Head block
    const headGeo = new THREE.BoxGeometry(0.44, 0.44, 0.44);
    const headMesh = new THREE.Mesh(headGeo, this.skinMaterial);
    this.headGroup.add(headMesh);

    // Stylized low-poly eyes
    const eyeGeo = new THREE.PlaneGeometry(0.07, 0.04);
    const leftEye = new THREE.Mesh(eyeGeo, this.darkAccentMaterial);
    leftEye.position.set(-0.11, 0.04, -0.222);
    leftEye.rotation.y = Math.PI;
    this.headGroup.add(leftEye);

    const rightEye = new THREE.Mesh(eyeGeo, this.darkAccentMaterial);
    rightEye.position.set(0.11, 0.04, -0.222);
    rightEye.rotation.y = Math.PI;
    this.headGroup.add(rightEye);
  }

  buildLimbs(bodyType) {
    const isFemale = bodyType === 'female';
    const armWidth = isFemale ? 0.14 : 0.17;
    const legWidth = isFemale ? 0.18 : 0.22;
    const shoulderX = isFemale ? 0.36 : 0.42;

    // --- ARMS ---
    // Left Arm Pivot at shoulder
    this.leftArmGroup.position.set(-shoulderX, 1.3, 0);
    const lUpperGeo = new THREE.BoxGeometry(armWidth, 0.32, armWidth);
    const lUpperMesh = new THREE.Mesh(lUpperGeo, this.upperClothMaterial);
    lUpperMesh.position.y = -0.16;
    this.leftArmGroup.add(lUpperMesh);

    const lForearmGeo = new THREE.BoxGeometry(armWidth * 0.9, 0.32, armWidth * 0.9);
    const lForearmMesh = new THREE.Mesh(lForearmGeo, this.skinMaterial);
    lForearmMesh.position.y = -0.44;
    this.leftArmGroup.add(lForearmMesh);

    // Right Arm Pivot at shoulder
    this.rightArmGroup.position.set(shoulderX, 1.3, 0);
    const rUpperGeo = new THREE.BoxGeometry(armWidth, 0.32, armWidth);
    const rUpperMesh = new THREE.Mesh(rUpperGeo, this.upperClothMaterial);
    rUpperMesh.position.y = -0.16;
    this.rightArmGroup.add(rUpperMesh);

    const rForearmGeo = new THREE.BoxGeometry(armWidth * 0.9, 0.32, armWidth * 0.9);
    const rForearmMesh = new THREE.Mesh(rForearmGeo, this.skinMaterial);
    rForearmMesh.position.y = -0.44;
    this.rightArmGroup.add(rForearmMesh);

    // --- LEGS ---
    const hipX = isFemale ? 0.16 : 0.18;
    // Left Leg Pivot at hip
    this.leftLegGroup.position.set(-hipX, 0.65, 0);
    const lLegGeo = new THREE.BoxGeometry(legWidth, 0.48, legWidth);
    const lLegMesh = new THREE.Mesh(lLegGeo, this.lowerClothMaterial);
    lLegMesh.position.y = -0.24;
    this.leftLegGroup.add(lLegMesh);

    const lShoeGeo = new THREE.BoxGeometry(legWidth * 1.05, 0.16, legWidth * 1.35);
    const lShoeMesh = new THREE.Mesh(lShoeGeo, this.shoeMaterial);
    lShoeMesh.position.set(0, -0.56, -0.04);
    this.leftLegGroup.add(lShoeMesh);

    // Right Leg Pivot at hip
    this.rightLegGroup.position.set(hipX, 0.65, 0);
    const rLegGeo = new THREE.BoxGeometry(legWidth, 0.48, legWidth);
    const rLegMesh = new THREE.Mesh(rLegGeo, this.lowerClothMaterial);
    rLegMesh.position.y = -0.24;
    this.rightLegGroup.add(rLegMesh);

    const rShoeGeo = new THREE.BoxGeometry(legWidth * 1.05, 0.16, legWidth * 1.35);
    const rShoeMesh = new THREE.Mesh(rShoeGeo, this.shoeMaterial);
    rShoeMesh.position.set(0, -0.56, -0.04);
    this.rightLegGroup.add(rShoeMesh);
  }

  buildHair(hairType) {
    if (hairType === 'bald') return;

    if (hairType === 'afro') {
      const afroGeo = new THREE.SphereGeometry(0.34, 7, 6);
      const afroMesh = new THREE.Mesh(afroGeo, this.hairMaterial);
      afroMesh.position.set(0, 0.12, 0);
      this.hairGroup.add(afroMesh);
    } else if (hairType === 'bun') {
      const baseGeo = new THREE.BoxGeometry(0.48, 0.22, 0.48);
      const baseMesh = new THREE.Mesh(baseGeo, this.hairMaterial);
      baseMesh.position.set(0, 0.16, 0);
      this.hairGroup.add(baseMesh);

      const bunGeo = new THREE.SphereGeometry(0.16, 6, 5);
      const bunMesh = new THREE.Mesh(bunGeo, this.hairMaterial);
      bunMesh.position.set(0, 0.32, 0.1);
      this.hairGroup.add(bunMesh);
    } else if (hairType === 'ponytail') {
      const baseGeo = new THREE.BoxGeometry(0.48, 0.22, 0.48);
      const baseMesh = new THREE.Mesh(baseGeo, this.hairMaterial);
      baseMesh.position.set(0, 0.16, 0);
      this.hairGroup.add(baseMesh);

      const tailGeo = new THREE.CylinderGeometry(0.08, 0.05, 0.44, 5);
      tailGeo.rotateX(Math.PI / 4);
      const tailMesh = new THREE.Mesh(tailGeo, this.hairMaterial);
      tailMesh.position.set(0, 0.12, 0.32);
      this.hairGroup.add(tailMesh);
    } else if (hairType === 'braids' || hairType === 'dreads') {
      const capGeo = new THREE.BoxGeometry(0.48, 0.24, 0.48);
      const capMesh = new THREE.Mesh(capGeo, this.hairMaterial);
      capMesh.position.set(0, 0.15, 0);
      this.hairGroup.add(capMesh);

      const strandGeo = new THREE.BoxGeometry(0.06, 0.42, 0.06);
      const positions = [
        [-0.23, -0.06, 0.1],
        [-0.23, -0.06, -0.08],
        [0.23, -0.06, 0.1],
        [0.23, -0.06, -0.08],
        [-0.1, -0.06, 0.23],
        [0.1, -0.06, 0.23],
      ];
      for (const [px, py, pz] of positions) {
        const strand = new THREE.Mesh(strandGeo, this.hairMaterial);
        strand.position.set(px, py, pz);
        this.hairGroup.add(strand);
      }
    } else {
      // Default / 'short'
      const shortGeo = new THREE.BoxGeometry(0.48, 0.2, 0.48);
      const shortMesh = new THREE.Mesh(shortGeo, this.hairMaterial);
      shortMesh.position.set(0, 0.16, 0.02);
      this.hairGroup.add(shortMesh);
    }
  }

  buildHat(hatType) {
    if (!hatType || hatType === 'none') return;

    if (hatType === 'cap') {
      const capDomeGeo = new THREE.SphereGeometry(0.26, 6, 5, 0, Math.PI * 2, 0, Math.PI / 2);
      const capDome = new THREE.Mesh(capDomeGeo, this.hatMaterial);
      capDome.position.set(0, 0.22, 0);
      this.hatGroup.add(capDome);

      const visorGeo = new THREE.BoxGeometry(0.36, 0.04, 0.22);
      const visor = new THREE.Mesh(visorGeo, this.hatMaterial);
      visor.position.set(0, 0.21, -0.26);
      this.hatGroup.add(visor);
    } else if (hatType === 'beanie') {
      const beanieGeo = new THREE.CylinderGeometry(0.24, 0.28, 0.28, 7);
      const beanie = new THREE.Mesh(beanieGeo, this.hatMaterial);
      beanie.position.set(0, 0.28, 0);
      this.hatGroup.add(beanie);
    } else if (hatType === 'tophat') {
      const brimGeo = new THREE.CylinderGeometry(0.38, 0.38, 0.04, 8);
      const brim = new THREE.Mesh(brimGeo, this.hatMaterial);
      brim.position.set(0, 0.23, 0);
      this.hatGroup.add(brim);

      const crownGeo = new THREE.CylinderGeometry(0.26, 0.26, 0.34, 8);
      const crown = new THREE.Mesh(crownGeo, this.hatMaterial);
      crown.position.set(0, 0.4, 0);
      this.hatGroup.add(crown);
    } else if (hatType === 'crown') {
      const crownGeo = new THREE.CylinderGeometry(0.28, 0.24, 0.18, 5);
      const crown = new THREE.Mesh(crownGeo, this.goldMaterial);
      crown.position.set(0, 0.3, 0);
      this.hatGroup.add(crown);
    }
  }

  buildAccessory(accType) {
    if (!accType || accType === 'none') return;

    if (accType === 'glasses') {
      const frameGeo = new THREE.BoxGeometry(0.46, 0.09, 0.04);
      const frame = new THREE.Mesh(frameGeo, this.darkAccentMaterial);
      frame.position.set(0, 0.04, -0.24);
      this.accessoryGroup.add(frame);
    } else if (accType === 'earrings') {
      const hoopGeo = new THREE.TorusGeometry(0.04, 0.015, 4, 8);
      const leftHoop = new THREE.Mesh(hoopGeo, this.goldMaterial);
      leftHoop.position.set(-0.24, -0.04, 0);
      leftHoop.rotation.y = Math.PI / 2;
      this.accessoryGroup.add(leftHoop);

      const rightHoop = new THREE.Mesh(hoopGeo, this.goldMaterial);
      rightHoop.position.set(0.24, -0.04, 0);
      rightHoop.rotation.y = Math.PI / 2;
      this.accessoryGroup.add(rightHoop);
    } else if (accType === 'beard') {
      const beardGeo = new THREE.BoxGeometry(0.32, 0.14, 0.22);
      const beard = new THREE.Mesh(beardGeo, this.hairMaterial);
      beard.position.set(0, -0.16, -0.14);
      this.accessoryGroup.add(beard);
    } else if (accType === 'scarf') {
      const scarfGeo = new THREE.CylinderGeometry(0.26, 0.26, 0.14, 6);
      const scarf = new THREE.Mesh(scarfGeo, this.hatMaterial);
      scarf.position.set(0, -0.18, 0);
      this.accessoryGroup.add(scarf);
    } else if (accType === 'backpack') {
      const packGeo = new THREE.BoxGeometry(0.42, 0.46, 0.22);
      const pack = new THREE.Mesh(packGeo, this.hatMaterial);
      pack.position.set(0, -0.5, 0.28);
      this.accessoryGroup.add(pack);
    }
  }

  setLane(laneX) {
    this.targetLaneX = laneX;
  }

  setJumpHeight(heightY) {
    this.group.position.y = heightY;
    this.isJumping = heightY > 0.05;
  }

  triggerOrbAbsorption() {
    this.absorbPulseTime = 0.28;
  }

  updateAnimation(dt, isRunning) {
    this.animTime += dt;
    this.isRunning = isRunning;

    // Smooth lateral lane tracking
    this.group.position.x += (this.targetLaneX - this.group.position.x) * Math.min(dt * CONFIG.LANES.CHANGE_SPEED, 1.0);

    // Subtle banking tilt when changing lanes
    const dx = this.targetLaneX - this.group.position.x;
    this.group.rotation.z = -dx * 0.12;

    // Visual feedback when absorbing an energy orb
    if (this.absorbPulseTime > 0) {
      this.absorbPulseTime -= dt;
      const pulse = Math.max(0, this.absorbPulseTime / 0.28);
      // Bright lime-cyan emission glow
      this.upperClothMaterial.emissive.setRGB(0.4 * pulse, 0.9 * pulse, 0.3 * pulse);
      const scaleBoost = 1.0 + 0.08 * Math.sin(pulse * Math.PI);
      this.bodyGroup.scale.set(scaleBoost, scaleBoost, scaleBoost);
    } else if (this.upperClothMaterial.emissive.r > 0) {
      this.upperClothMaterial.emissive.setRGB(0, 0, 0);
      this.bodyGroup.scale.set(1, 1, 1);
    }

    if (this.isJumping) {
      // Jumping pose: arms back, legs tucked
      this.leftArmGroup.rotation.x = -0.6;
      this.rightArmGroup.rotation.x = -0.6;
      this.leftLegGroup.rotation.x = 0.5;
      this.rightLegGroup.rotation.x = 0.5;
      this.headGroup.position.y = 1.55;
    } else if (this.isRunning) {
      // Running gait: alternating limb swing
      const swingSpeed = 16.0;
      const armSwing = Math.sin(this.animTime * swingSpeed) * 0.8;
      const legSwing = Math.sin(this.animTime * swingSpeed) * 0.9;
      const verticalBob = Math.abs(Math.cos(this.animTime * swingSpeed)) * 0.08;

      this.leftArmGroup.rotation.x = armSwing;
      this.rightArmGroup.rotation.x = -armSwing;
      this.leftLegGroup.rotation.x = -legSwing;
      this.rightLegGroup.rotation.x = legSwing;
      this.headGroup.position.y = 1.55 + verticalBob;
    } else {
      // Idle menu breathing
      const breath = Math.sin(this.animTime * 2.5) * 0.02;
      this.headGroup.position.y = 1.55 + breath;
      this.leftArmGroup.rotation.x = 0;
      this.rightArmGroup.rotation.x = 0;
      this.leftLegGroup.rotation.x = 0;
      this.rightLegGroup.rotation.x = 0;
    }
  }

  dispose() {
    this.skinMaterial.dispose();
    this.upperClothMaterial.dispose();
    this.lowerClothMaterial.dispose();
    this.hairMaterial.dispose();
    this.shoeMaterial.dispose();
    this.hatMaterial.dispose();
    this.goldMaterial.dispose();
    this.darkAccentMaterial.dispose();

    if (this.group.parent) {
      this.group.parent.remove(this.group);
    }
  }
}
